/**
 * Utility functions and state management for building and managing hierarchical group structures
 * Handles drag and drop functionality, searching, and tree manipulation
 *
 * @param {Array} data - Initial tree data structure
 * @param {Function} emit - Event emitter function
 */
import { flatMap, isEmpty } from "lodash";
import { computed, onMounted, ref } from "vue";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { extractInstruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
import { useGroupBuilderStore } from "@/Stores/groupBuilder";

export function useGroupBuilder(data, emit, storeKey) {
    const filteredItems = ref(data);
    const groupBuilderStore = useGroupBuilderStore();

    /**
     * Recursively finds a node in the tree by ID
     */
    const findNode = (data, itemId) => {
        for (const item of data) {
            if (item.id === itemId) return item;
            if (hasChildren(item)) {
                const result = findNode(item.children ?? [], itemId);
                if (result) return result;
            }
        }
    };

    /**
     * Finds the parent node ID for a given target node
     */
    const findParentNodeId = (data, targetId) => {
        const recursiveSearch = (nodes, parentId = null) => {
            for (const node of nodes) {
                if (node.id === targetId) return parentId;
                if (node.children?.length > 0) {
                    const found = recursiveSearch(node.children, node.id);
                    if (found) return found;
                }
            }
            return null;
        };
        return recursiveSearch(data);
    };

    /**
     * Gets the path of parent IDs leading to a target item
     */
    const getPathToItem = ({ current, targetId, parentIds }) => {
        for (const item of current) {
            if (item.id === targetId) return parentIds;
            const nested = getPathToItem({
                current: item.children ?? [],
                targetId,
                parentIds: [...parentIds, item.id],
            });
            if (nested) return nested;
        }
    };

    /**
     * Checks if a node has child nodes
     */
    const hasChildren = (item) => (item.children ?? []).length > 0;

    /**
     * Toggles node expansion state
     */
    const toggleNode = (node) => {
        node.isExpanded = !node.isExpanded;

        // Update the expanded items
        handleExpandedItems();
    };

    /**
     * Recursively searches nodes and their children for a query string
     */
    const searchNode = (node, query) => {
        const isMatch =
            node.name.toLowerCase().includes(query.toLowerCase()) ||
            node.identifier?.toLowerCase().includes(query.toLowerCase());
        const filteredChildren =
            node.children
                ?.map((child) => searchNode(child, query))
                .filter(Boolean) ?? [];

        if (isMatch || filteredChildren.length) {
            return {
                ...node,
                isExpanded: filteredChildren.length > 0,
                children: filteredChildren,
            };
        }
        return null;
    };

    /**
     * Filters tree based on search query
     */
    const search = (data, query) => {
        // Get the results
        filteredItems.value = isEmpty(query)
            ? data
            : data.map((node) => searchNode(node, query)).filter(Boolean);

        // Update the expanded items
        handleExpandedItems();
    };

    /**
     * Flattens nested tree structure into array
     */
    const flattenItems = (array) =>
        flatMap(array, (item) => [item, ...flattenItems(item.children ?? [])]);

    /**
     * Tracks expanded item IDs
     */
    const expandedItems = computed(() => {
        return groupBuilderStore[storeKey] || [];
    });

    const handleExpandedItems = () => {
        if (!groupBuilderStore[storeKey]) {
            console.error(
                "The specified store key is not present in the group builder store."
            );
        }

        groupBuilderStore[storeKey] = flattenItems(filteredItems.value)
            .filter((item) => item.isExpanded)
            .map((item) => item.id);
    };

    /**
     * Emits move events for drag and drop operations
     */
    const handleMoveItem = (id, parentId) =>
        emit("move:item", { id, parentId });

    /**
     * Updates tree structure based on drag and drop actions
     */
    const updateTree = (nodes, action) => {
        const item = findNode(nodes, action.itemId);
        if (!item || action.itemId === action.targetId) return nodes;

        if (action.type === "instruction") {
            const { instruction } = action;

            if (instruction.type === "reparent") {
                const path = getPathToItem({
                    current: nodes,
                    targetId: action.targetId,
                });
                if (!path) {
                    console.error("Missing path");
                    return;
                }
            }

            const parentId =
                instruction.type === "make-child"
                    ? action.targetId
                    : findParentNodeId(filteredItems.value, action.targetId);

            if (parentId !== item.parent_group) {
                handleMoveItem(item.id, parentId);
            }

            return nodes;
        }

        return nodes;
    };

    /**
     * Initializes drag and drop monitoring
     */
    const initialise = () =>
        combine(
            monitorForElements({
                onDrop({ location, source }) {
                    if (!location.current.dropTargets.length) return;

                    const target = location.current.dropTargets[0];
                    const instruction = extractInstruction(target.data);

                    if (instruction) {
                        filteredItems.value =
                            updateTree(filteredItems.value, {
                                type: "instruction",
                                instruction,
                                itemId: source.data.id,
                                targetId: target.data.id,
                                target,
                            }) ?? [];
                    }
                },
            })
        );

    /**
     * Initializes the expanded state for all items in the tree
     * Recursively checks each item and its children to set their expanded state
     * based on whether their ID is included in the expandedItems array
     */
    const initExpandedItems = () => {
        const setExpandedState = (items) => {
            items.forEach((item) => {
                item.isExpanded = expandedItems.value.includes(item.id);
                if (item.children?.length) {
                    setExpandedState(item.children);
                }
            });
        };

        setExpandedState(filteredItems.value);
    };

    onMounted(() => {
        initExpandedItems();
    });

    return {
        toggleNode,
        filteredItems,
        expandedItems,
        initialise,
        search,
    };
}
